import {useEffect} from "react"
import {Link, Navigate} from "react-router-dom"
import {default as cx} from "classnames"

import SubHeader from "./SubHeader.jsx"
import {getToken} from "../utilities/useToken.jsx"
import {OSR_ROLES, NHSR_ROLES, UNIT_DASHBOARD_ROLES, SYSTEM_DASHBOARD_ROLES} from "../utilities/roleAccess.jsx"

function HomeCard({header, text, icon, path}) {
    return (
        <div className="col-xl-3">
            <Link to={path} className="card bg-light flex-center p-6 card-bordered border-hover-primary">
                <div className="card-header border-0 mb-3">
                    <div className="card-title">
                        <i className={cx("fas text-primary fs-3x", icon)}></i>
                    </div>
                </div>
                <div className="card-body py-2 py-lg-0">
                    <div className="text-gray-900 fw-bold fs-2 text-center">
                        {header}
                        <span className="fs-6 fw-semibold d-flex flex-column pt-2">{text}</span>
                    </div>
                </div>
            </Link>
        </div>
    )
}

const HOME_CARDS = [
    {
        header: "Surveys & Reports",
        text: "Administer surveys and reports to gather feedback and track progress.",
        icon: "fa-clipboard-list",
        path: "/surveys",
        roleRequired: [...OSR_ROLES, ...NHSR_ROLES],
    },
    {
        header: "Metrics & Scores",
        text: "Track OSR scores and progress as new hires progress through orientation.",
        icon: "fa-chart-simple",
        path: "/metrics",
        roleRequired: [...UNIT_DASHBOARD_ROLES, ...SYSTEM_DASHBOARD_ROLES],
    },
]

function Home() {
    const {role} = getToken(true) || {}
    const visibleCards = HOME_CARDS.filter((c) => c.roleRequired.includes(role))

    useEffect(() => {
        document.title = "Home"
    }, [])

    if (role === "Educator") {
        return <Navigate to="/educator-orientations" replace />
    }

    if (role === "Orientee") {
        return <Navigate to="/home-orientee" replace />
    }

    if (role === "Preceptor") {
        return <Navigate to="/preceptor-orientations" replace />
    }

    if (role === "Administrator") {
        return <Navigate to="/metrics/dashboard/overview" replace />
    }

    if (role === "Enterprise Admin") {
        return (
            <>
                <SubHeader title1="Welcome to OARS" title2="Orientation Assessment Reporting System" icon="fa-home" />
                <div className="container-fluid p-0">
                    <div className="container py-10">
                        <h2 className="text-center mb-8">Please make a selection below</h2>
                        <div className="row g-5 g-xl-8 justify-content-center">
                            <HomeCard
                                header={"User Accounts"}
                                text={"Manage OARS Users"}
                                icon={"fa-users"}
                                path={"/admin/users"}
                            />
                            {window.__APP_CONFIG__?.ENV && window.__APP_CONFIG__.ENV !== "prod" && (
                                <HomeCard
                                    header={"Adopt a Persona"}
                                    text={"Run as a specific user"}
                                    icon={"fa-user-secret"}
                                    path={"/adopt-persona"}
                                />
                            )}
                            <HomeCard
                                header={"View Organizations"}
                                text={"Manage Organizations"}
                                icon={"fa-sitemap"}
                                path={"/admin/organization"}
                            />
                            <HomeCard
                                header={"Metrics & Scores"}
                                text={"Check Overall Progress"}
                                icon={"fa-chart-simple"}
                                path={"/metrics/dashboard/overview"}
                            />
                        </div>
                    </div>
                </div>
            </>
        )
    }

    if (role === "Metrics & Reports") {
        return <Navigate to="/metrics/dashboard/overview" replace />
    }

    if (visibleCards.length === 1) {
        return <Navigate to={visibleCards[0].path} replace />
    }

    return (
        <>
            <SubHeader title1="Welcome to OARS" title2="Orientation Assessment Reporting System" icon="fa-home" />
            <div className="container-fluid p-0">
                <div className="container py-10">
                    <h2 className="text-center mb-8">Please make a selection below</h2>
                    <div className="row g-5 g-xl-8 justify-content-center">
                        {visibleCards.map((card) => (
                            <HomeCard key={card.path} {...card} />
                        ))}
                    </div>
                </div>
            </div>
        </>
    )
}

export default Home
